<template>
    <div class="flex flex-col p-5 gap-y-5">
        <div class="flex justify-between">
            <div class="flex flex-1">
                <h1 class="page-header">Access Plan Modules</h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div class="text-2xl">/</div>
                    <div class="ml-2 text-xl pt-1">{{ planModules.length }}</div>
                </div>
            </div>
            <div class="flex items-center">
                <div class="flex items-center">
                    <filters
                        data-type="plan-modules"
                        sort-by="name"
                        portal="no-plan-modules-found"
                        :show-client-filter="false"
                        :selected-view-option="layout"
                        :show-view-options="true"
                        :show-search="false"
                        :show-filters="false"
                        @view-change="setLayout">
                    </filters>
                    <slot name="action-buttons"></slot>
                </div>
                <button
                    @click="showAddModal"
                    class="ml-4 flex items-center btn btn-small btn-blue focus:outline-none">
                    <inline-svg classes="w-4 h-4 text-white fill-current mr-2" name="add"></inline-svg>
                    Add Plan Module
                </button>
            </div>
        </div>

        <!-- Grid View -->
        <div v-if="layout === 'grid' && planModules.length" class="flex flex-wrap gap-x-5 gap-y-3">
            <div class="card no-scale border rounded-md" v-for="plan in planModules" :key="plan.id">
                <div class="w-72 h-full box box-green hover:bg-gray-100">
                    <div class="relative px-5 py-6 flex flex-col">
                        <div class="absolute top-3 right-2 flex items-center justify-between space-x-3 z-50">
                            <button
                                type="button"
                                @click="openEditModal(plan)"
                                class="cursor-pointer"
                                title="Edit Plan Module">
                                <inline-svg name="edit"></inline-svg>
                            </button>
                            <button
                                type="button"
                                @click="confirmDelete(plan.id)"
                                class="cursor-pointer"
                                title="Delete Plan Module">
                                <inline-svg name="trash"></inline-svg>
                            </button>
                        </div>
                        <div class="text-xl font-semibold mb-2">{{ getProductName(plan.stripe_product_id) }}</div>
                        <div class="flex-1">
                            <div class="text-md text-gray-700">
                                <p>Modules: {{ plan.modules ? plan.modules.map(m => getModuleName(m.id)).join(', ') : getModuleName(plan.module_id) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Table View -->
        <div v-if="layout === 'table' && planModules.length">
            <table class="mt-2 w-full">
                <thead class="cursor-pointer">
                    <th class="text-left pl-4">Product Name</th>
                    <th class="text-left px-6">Modules</th>
                    <th class="px-6">Actions</th>
                </thead>
                <tbody>
                    <tr v-for="plan in planModules" :key="plan.id">
                        <td class="pl-4 py-3">{{ getProductName(plan.stripe_product_id) }}</td>
                        <td class="px-6 py-3">{{ plan.modules ? plan.modules.map(m => getModuleName(m.id)).join(', ') : getModuleName(plan.module_id) }}</td>
                        <td class="px-6 py-3">
                            <div class="flex justify-end space-x-3">
                                <button
                                    @click="openEditModal(plan)"
                                    title="Edit Plan Module"
                                    class="btn btn-action">
                                    <inline-svg name="edit"></inline-svg>
                                </button>
                                <button
                                    @click="confirmDelete(plan.id)"
                                    class="btn btn-action btn-red text-white"
                                    title="Delete Plan Module">
                                    <inline-svg classes="w-4 h-4 text-white fill-current" name="trash"></inline-svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="!planModules.length" class="text-gray-500">No plan modules found.</div>
        <portal-target name="no-plan-modules-found"></portal-target>

        <!-- Delete Confirmation Modal -->
        <modal name="delete-confirmation-modal" :adaptive="true" :scrollable="true" height="auto">
            <div class="p-6">
                <h2 class="text-lg font-bold mb-4">Confirm Deletion</h2>
                <p class="mb-6">Are you sure you want to delete this plan module?</p>
                <div class="flex justify-end space-x-3">
                    <button @click="$modal.hide('delete-confirmation-modal')" class="btn btn-secondary">
                        Cancel
                    </button>
                    <button @click="processDelete" class="btn btn-red">
                        Delete
                    </button>
                </div>
            </div>
        </modal>

        <modal name="add-plan-module-modal" :adaptive="true" :scrollable="true" height="auto" @closed="resetAddForm">
            <div class="p-6">
                <h2 class="text-lg font-bold mb-4">Add Plan Module</h2>
                <form @submit.prevent="addPlanModule">
                    <div class="mb-4">
                        <label class="block mb-1">Stripe Product</label>
                        <select v-model="form.stripe_product_id" class="form-input w-full" required>
                            <option value="" disabled>Select Stripe Product</option>
                            <option v-for="product in stripeProducts" :key="product.id" :value="product.id">
                                {{ product.name }}
                            </option>
                        </select>
                    </div>
                    <div class="mb-4">
                        <label class="block mb-1">Modules</label>
                        <select v-model="form.module_ids" class="form-input w-full" multiple required>
                            <option v-for="module in modules" :key="module.id" :value="module.id">
                                {{ module.display_name }}
                            </option>
                        </select>
                    </div>
                    <div class="flex justify-end">
                        <button type="button" class="btn btn-secondary mr-2" @click="$modal.hide('add-plan-module-modal')">Cancel</button>
                        <button type="submit" class="btn btn-blue">Add</button>
                    </div>
                </form>
            </div>
        </modal>

        <modal name="edit-plan-module-modal" :adaptive="true" :scrollable="true" height="auto" @closed="resetEditForm">
            <div class="p-6">
                <h2 class="text-lg font-bold mb-4">Edit Plan Module</h2>
                <form @submit.prevent="updatePlanModule">
                    <div class="mb-4">
                        <label class="block mb-1">Stripe Product</label>
                        <select v-model="editForm.stripe_product_id" class="form-input w-full" required>
                            <option value="" disabled>Select Stripe Product</option>
                            <option v-for="product in stripeProducts" :key="product.id" :value="product.id">
                                {{ product.name }}
                            </option>
                        </select>
                    </div>
                    <div class="mb-4">
                        <label class="block mb-1">Modules</label>
                        <select v-model="editForm.module_ids" class="form-input w-full" multiple required>
                            <option v-for="module in modules" :key="module.id" :value="module.id">
                                {{ module.display_name }}
                            </option>
                        </select>
                    </div>
                    <div class="flex justify-end">
                        <button type="button" class="btn bg-gray-200 text-gray-800 mr-2" @click="$modal.hide('edit-plan-module-modal')">Cancel</button>
                        <button type="submit" class="btn btn-blue">Update</button>
                    </div>
                </form>
            </div>
        </modal>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {

    props: {
        showHeader: {
            type: Boolean,
            default: true
        },
        smallHeader: {
            type: Boolean,
            default: false
        },
        showFilters: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            layout: 'grid',
            modules: [],
            stripeProducts: [],
            deleteId: null,
            form: {
                stripe_product_id: '',
                module_ids: [] // now an array
            },
            editForm: {
                id: '',
                stripe_product_id: '',
                module_ids: [] // now an array
            }
        }
    },
    computed: {
        ...mapState('plan-modules', {
            planModules: state => state.filtered.length ? state.filtered : (state.all || [])
        })
    },
    methods: {
        ...mapActions('plan-modules', ['setPlanModules']),
        setLayout(layout) {
            this.layout = layout;
            localStorage.setItem('reopt_plan_modules_layout', layout);
        },
        async fetchData() {
            try {
                const response = await axios.get('/admin/plan-modules');
                this.setPlanModules(response.data.planModules || []);
                this.stripeProducts = response.data.stripeProducts || [];
                this.modules = response.data.modules || [];
            } catch (error) {
                console.error('Error fetching plan modules:', error);
            }
        },
        async addPlanModule() {
            try {
                await axios.post('/admin/plan-modules', {
                    stripe_product_id: Number(this.form.stripe_product_id),
                    module_ids: this.form.module_ids
                });
                this.$modal.hide('add-plan-module-modal');
                this.fetchData();
                this.resetAddForm();
            } catch (error) {
                console.error('Error adding plan module:', error);
            }
        },
        showAddModal() {
            this.$modal.show('add-plan-module-modal');
        },
        openEditModal(plan) {
            this.editForm.id = plan.id;
            this.editForm.stripe_product_id = plan.stripe_product_id;
            // If plan.modules is an array of module objects
            this.editForm.module_ids = plan.modules ? plan.modules.map(m => m.id) : [plan.module_id];
            this.$modal.show('edit-plan-module-modal');
        },
        resetAddForm() {
            this.form.stripe_product_id = '';
            this.form.module_ids = [];
        },
        resetEditForm() {
            this.editForm.id = null;
            this.editForm.stripe_product_id = '';
            this.editForm.module_ids = [];
        },
        async updatePlanModule() {
            try {
                await axios.put(`/admin/plan-modules/${this.editForm.id}`, {
                    stripe_product_id: Number(this.editForm.stripe_product_id),
                    module_ids: this.editForm.module_ids
                });
                this.$modal.hide('edit-plan-module-modal');
                this.fetchData();
            } catch (error) {
                console.error('Error updating plan module:', error);
            }
        },
        confirmDelete(id) {
            this.deleteId = id;
            this.$modal.show('delete-confirmation-modal');
        },
        async processDelete() {
            try {
                await axios.delete(`/admin/plan-modules/${this.deleteId}`);
                this.$modal.hide('delete-confirmation-modal');
                this.fetchData();
                this.deleteId = null;
            } catch (error) {
                console.error('Error deleting plan module:', error);
            }
        },
        deletePlanModule(id) {
            return axios.delete(`/admin/plan-modules/${id}`);
        },
        getProductName(stripe_product_id) {
            if (!stripe_product_id) return 'N/A';
            const stripeProduct = this.stripeProducts.find(p => p.id == stripe_product_id);
            if (stripeProduct) {
                return stripeProduct.name || stripeProduct.id;
            }
            return `Product (${stripe_product_id})`;
        },
        getModuleName(module_id) {
            const module = this.modules.find(m => m.id == module_id);
            return module ? module.display_name : module_id;
        }
    },
    mounted() {
        if (localStorage.getItem('reopt_plan_modules_layout')) {
            this.layout = localStorage.getItem('reopt_plan_modules_layout');
        }
        this.fetchData();
    },
    watch: {
        layout(newVal) {
            localStorage.setItem('reopt_plan_modules_layout', newVal);
        }
    },
}
</script>

<style scoped>
table {
    border-collapse: collapse;
    width: 100%;
}

thead {
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
}

tr:hover {
    background-color: #f8fafc;
}

.page-header {
    font-size: 2rem;
    font-weight: bold;
}
</style>
